import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { Crown, Shield, AlertCircle, Info, User as UserIcon } from 'lucide-react';
import { canReassignBillingAdmin, canUserReassignBillingAdmin } from '../utils/billingAdmin';
import { projectId, publicAnonKey } from '../utils/supabase/info';
import type { User, SubscriptionPlan } from '../types';

interface BillingAdminAssignmentProps {
  currentUser: User;
  firmId: string;
  firmPlan: SubscriptionPlan;
  allUsers: User[];
  accessToken: string;
  onAssignmentChanged: () => void;
}

export function BillingAdminAssignment({
  currentUser,
  firmId,
  firmPlan,
  allUsers,
  accessToken,
  onAssignmentChanged,
}: BillingAdminAssignmentProps) {
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Get current Billing Admin
  const currentBillingAdmin = allUsers.find(u => u.isBillingAdmin);
  
  // Check if current user can reassign
  const canReassign = canUserReassignBillingAdmin(currentUser, firmPlan);
  const isReassignable = canReassignBillingAdmin(firmPlan);

  // Filter eligible users (CF Admins and Consultants, but not System Admins)
  const eligibleUsers = allUsers.filter(u => 
    (u.role === 'consulting_firm_admin' || u.role === 'consultant') && 
    u.status === 'active' &&
    u.id !== currentBillingAdmin?.id
  );

  const handleReassign = async () => {
    if (!selectedUserId) {
      setError('Please select a user');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/billing/reassign-billing-admin`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            firmId,
            newBillingAdminId: selectedUserId,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to reassign Billing Admin');
      }

      setShowReassignDialog(false);
      setSelectedUserId('');
      onAssignmentChanged();
    } catch (err: any) {
      console.error('Error reassigning Billing Admin:', err);
      setError(err.message || 'Failed to reassign Billing Admin');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-yellow-500" />
            Billing Admin
          </CardTitle>
          <CardDescription>
            The Billing Admin manages subscription changes, payment methods, and approves upgrade requests
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current Billing Admin */}
          {currentBillingAdmin ? (
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-yellow-100 dark:bg-yellow-900/20 flex items-center justify-center">
                  <Crown className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{currentBillingAdmin.name || currentBillingAdmin.email}</p>
                    <Badge variant="secondary" className="gap-1">
                      <Shield className="h-3 w-3" />
                      Billing Admin
                    </Badge>
                  </div>
                  <p className="text-muted-foreground">{currentBillingAdmin.email}</p>
                </div>
              </div>

              {canReassign && eligibleUsers.length > 0 && (
                <Button
                  onClick={() => setShowReassignDialog(true)}
                  variant="outline"
                  size="sm"
                >
                  Reassign
                </Button>
              )}
            </div>
          ) : (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                No Billing Admin assigned. Please contact support.
              </AlertDescription>
            </Alert>
          )}

          {/* Info about Solo plan restriction */}
          {!isReassignable && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                On the Solo plan, the Billing Admin role cannot be reassigned. Upgrade to Starter or higher to assign a separate Billing Admin.
              </AlertDescription>
            </Alert>
          )}

          {/* Info about Billing Admin responsibilities */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-medium">Billing Admin Responsibilities:</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>Approve or deny subscription upgrade requests</li>
                  <li>Manage payment methods and billing information</li>
                  <li>View and download invoices</li>
                  <li>Purchase additional company slot addons</li>
                  <li>Cancel subscription (with confirmation)</li>
                </ul>
              </div>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Reassign Dialog */}
      <Dialog open={showReassignDialog} onOpenChange={setShowReassignDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-yellow-500" />
              Reassign Billing Admin
            </DialogTitle>
            <DialogDescription>
              Select a new Billing Admin for your consulting firm. This person will have full control over billing and subscription management.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-3">
              <Label>Select New Billing Admin</Label>
              <RadioGroup value={selectedUserId} onValueChange={setSelectedUserId}>
                {eligibleUsers.map((user) => (
                  <div key={user.id} className="flex items-start space-x-2 border rounded-lg p-4">
                    <RadioGroupItem value={user.id} id={user.id} className="mt-1" />
                    <div className="flex-1">
                      <Label htmlFor={user.id} className="font-medium cursor-pointer">
                        {user.name || user.email}
                      </Label>
                      <p className="text-muted-foreground">{user.email}</p>
                      <p className="text-muted-foreground">
                        {user.role === 'consulting_firm_admin' ? 'Firm Admin' : 'Consultant'}
                      </p>
                    </div>
                  </div>
                ))}
              </RadioGroup>

              {eligibleUsers.length === 0 && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    No eligible users found. Only active CF Admins and Consultants can be assigned as Billing Admin.
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Warning:</strong> You will lose Billing Admin privileges when you reassign this role. Only the new Billing Admin can reassign it again.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowReassignDialog(false);
                setSelectedUserId('');
                setError('');
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReassign}
              disabled={loading || !selectedUserId}
            >
              {loading ? 'Reassigning...' : 'Confirm Reassignment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}